/**
 * actionCardKit — las piezas visuales que comparten TODAS las acciones.
 *
 * El tablero de Seguimientos ya había resuelto cómo se lee una acción de un
 * vistazo: una franja de color que dice la urgencia, unas filas etiquetadas
 * con el contexto, y los botones a la vista en vez de escondidos tras unos
 * puntos suspensivos. Esto es esa gramática, extraída para que un pago, una
 * obra y un seguimiento se lean igual.
 *
 * Aquí no se decide QUÉ se enseña —eso es de `lib/actionContext.js`, que
 * conoce cada tipo— solo CÓMO. Separarlo evita la tentación de inventar un
 * dato para llenar un hueco: si el contexto no trae valor, la fila enseña el
 * hueco y ya.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ClockCounterClockwise } from "@phosphor-icons/react";

/** Botón secundario: 44px reales, como en Seguimientos. */
export const ACTION_BTN =
  "inline-flex items-center justify-center gap-1.5 text-xs min-h-[44px] px-3 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-900 dark:hover:border-stone-300 disabled:opacity-50";

/** La acción que resuelve la tarjeta. Una sola por tarjeta. */
export const ACTION_BTN_PRIMARY =
  "inline-flex items-center justify-center gap-1.5 text-xs min-h-[44px] px-3 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50 dark:bg-stone-100 dark:text-stone-900";

/**
 * Una fila de contexto. Sin valor se enseña "—": el hueco es información
 * (no lo sabemos) y rellenarlo con algo plausible sería mentir.
 */
export function ContextRow({ label, value, testId }) {
  return (
    <div className="min-w-0">
      <span className="uppercase tracking-widest text-[10px] text-stone-400 mr-1">
        {label}:
      </span>
      <span className="text-stone-600 dark:text-stone-300" data-testid={testId}>
        {value || "—"}
      </span>
    </div>
  );
}

/**
 * La rejilla de contexto. Una columna en el móvil, dos en tableta y tres en
 * el escritorio: la fila ocupa todo el ancho, así que apilar el contexto en
 * dos columnas dejaba media tarjeta vacía y la estiraba a lo alto. Repartirlo
 * en tres deja la fila ancha y baja, que es como se barre una lista.
 */
export function ContextRows({ rows, idPrefix }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-1 gap-x-4 text-xs min-w-0 lg:flex-1"
         data-testid={`${idPrefix}-context`}>
      {rows.map((r) => (
        <ContextRow
          key={r.key}
          label={r.label}
          value={r.value}
          testId={`${idPrefix}-${r.key}`}
        />
      ))}
    </div>
  );
}

/** Enlace con la misma pinta que los botones. */
export function ActionLink({ to, href, children, testId }) {
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
         className={ACTION_BTN} data-testid={testId}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={ACTION_BTN} data-testid={testId}>
      {children}
    </Link>
  );
}

/**
 * "Reprogramar" con sus presets. Idéntico en un seguimiento y en una alerta:
 * posponer es posponer, y la lista de plazos tiene un solo dueño
 * (`lib/actionCta.js`).
 */
export function SnoozeButton({
  label = "Reprogramar", presets, busy, onPick, testId,
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={ACTION_BTN}
        data-testid={testId}
      >
        <ClockCounterClockwise size={14} /> {label}
      </button>
      {open && (
        <div
          className="absolute left-0 bottom-full mb-1 z-20 min-w-[150px] rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 shadow-md py-1"
          data-testid={`${testId}-menu`}
        >
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              disabled={busy}
              className="w-full text-left text-xs px-3 min-h-[44px] hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-50"
              onClick={() => { setOpen(false); onPick(p.key); }}
              data-testid={`${testId}-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Los botones, siempre visibles. A la derecha como en Seguimientos: en una
 * fila ancha el ojo termina el contexto y se encuentra ahí la acción. En
 * escritorio no bajan de línea (`ACTION_FOOTER` los pone al lado del
 * contexto), así que la tarjeta queda ancha y baja en vez de estirarse.
 */
export function ActionButtonRow({ children, idPrefix }) {
  return (
    <div className="flex flex-wrap gap-2 justify-end lg:shrink-0"
         data-testid={`${idPrefix}-actions`}>
      {children}
    </div>
  );
}

/**
 * El pie de la tarjeta: contexto a la izquierda, botones a la derecha.
 *
 * Apilados ocupaban el doble de alto y dejaban muerto todo el centro de una
 * fila de mil píxeles. En el móvil vuelven a apilarse, que es donde eso sí
 * tiene sentido.
 */
export const ACTION_FOOTER =
  "mt-2 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-6";
